'use client'

import { Link } from 'react-router-dom'
import { useState } from 'react'
import { Trash2, Plus, Minus, ShoppingBag, Tag, ArrowRight, AlertCircle } from 'lucide-react'
import { useCartStore } from '@/stores/cart'
import { useCurrencyStore } from '@/stores/currency'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card } from '@/components/ui/card'
import ECommerceHeader from '@/components/layout/ECommerceHeader'
import Footer from '@/components/layout/Footer'
import CartExpiryNotice from '@/components/cart/CartExpiryNotice'

// Mock coupons for demo
const VALID_COUPONS: Record<string, { type: 'percentage' | 'fixed'; value: number; minOrder?: number }> = {
  SAVE10: { type: 'percentage', value: 10, minOrder: 500 },
  FLAT100: { type: 'fixed', value: 100, minOrder: 1000 },
  WELCOME20: { type: 'percentage', value: 20, minOrder: 300 },
}

export default function CartPage() {
  const { items, total, item_count, coupon_code, discount, updateQuantity, removeItem, applyCoupon, removeCoupon, clearCart } =
    useCartStore()
  const getSymbol = useCurrencyStore((state) => state.getSymbol)
  const convert = useCurrencyStore((state) => state.convert)

  const [couponInput, setCouponInput] = useState('')
  const [couponError, setCouponError] = useState('')
  const [couponSuccess, setCouponSuccess] = useState('')

  const handleApplyCoupon = () => {
    setCouponError('')
    setCouponSuccess('')

    const coupon = VALID_COUPONS[couponInput.toUpperCase()]
    if (!coupon) {
      setCouponError('Invalid coupon code')
      return
    }

    if (coupon.minOrder && total < coupon.minOrder) {
      setCouponError(`Minimum order of ${getSymbol()}${coupon.minOrder} required`)
      return
    }

    const discountAmount = coupon.type === 'percentage' ? (total * coupon.value) / 100 : coupon.value
    applyCoupon(couponInput.toUpperCase(), discountAmount)
    setCouponSuccess(`Coupon applied! You save ${getSymbol()}${discountAmount.toLocaleString()}`)
    setCouponInput('')
  }

  const handleRemoveCoupon = () => {
    removeCoupon()
    setCouponSuccess('')
    setCouponError('')
  }

  const shippingCost = total >= 500 ? 0 : 60
  const finalTotal = total - discount + shippingCost

  return (
    <div className="min-h-screen bg-background">
      <ECommerceHeader />

      <main className="container mx-auto px-4 py-8">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-8">Shopping Cart</h1>

        {items.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingBag className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">Your cart is empty</h2>
            <p className="text-muted-foreground mb-6">Add some items to get started</p>
            <Link to="/">
              <Button variant="primary">
                <ShoppingBag className="h-4 w-4 mr-2" />
                Start Shopping
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Expiry Notice */}
            <div className="lg:col-span-3">
              <CartExpiryNotice />
            </div>

            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between pb-4 border-b border-border">
                <p className="text-muted-foreground">
                  {item_count} {item_count === 1 ? 'item' : 'items'} in cart
                </p>
                <Button variant="ghost" size="sm" onClick={clearCart} className="text-destructive hover:text-destructive">
                  Clear Cart
                </Button>
              </div>

              {/* Items */}
              {items.map((item) => (
                <Card key={item.product_id} className="p-4">
                  <div className="flex gap-4">
                    {/* Product Image */}
                    <div className="w-24 h-24 rounded-md overflow-hidden bg-muted flex-shrink-0">
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                    </div>

                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-foreground line-clamp-2">{item.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{item.vendor_name}</p>

                      {/* Price */}
                      <div className="flex items-baseline gap-2 mt-2">
                        <span className="font-semibold text-brand-orange">
                          {getSymbol()}
                          {convert(item.price).toLocaleString()}
                        </span>
                        {item.original_price > item.price && (
                          <span className="text-sm text-muted-foreground line-through">
                            {getSymbol()}
                            {convert(item.original_price).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Quantity & Actions */}
                    <div className="flex flex-col items-end gap-2">
                      {/* Quantity Selector */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Subtotal */}
                      <p className="text-sm font-medium">
                        Subtotal: {getSymbol()}
                        {convert(item.price * item.quantity).toLocaleString()}
                      </p>

                      {/* Remove Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(item.product_id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="p-6 sticky top-24">
                <h2 className="text-lg font-semibold text-foreground mb-4">Order Summary</h2>

                {/* Coupon Input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    <Tag className="h-4 w-4 inline mr-1" />
                    Have a coupon?
                  </label>
                  {coupon_code ? (
                    <div className="flex items-center justify-between p-3 bg-success/10 rounded-md">
                      <div>
                        <span className="font-medium text-success">{coupon_code}</span>
                        <p className="text-xs text-muted-foreground">
                          You save {getSymbol()}
                          {discount.toLocaleString()}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={handleRemoveCoupon}>
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter coupon code"
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value)}
                        className="flex-1"
                      />
                      <Button variant="outline" onClick={handleApplyCoupon}>
                        Apply
                      </Button>
                    </div>
                  )}
                  {couponError && (
                    <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {couponError}
                    </p>
                  )}
                  {couponSuccess && <p className="text-xs text-success mt-1">{couponSuccess}</p>}
                </div>

                {/* Price Breakdown */}
                <div className="space-y-3 py-4 border-t border-border">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>
                      {getSymbol()}
                      {convert(total).toLocaleString()}
                    </span>
                  </div>

                  {discount > 0 && (
                    <div className="flex justify-between text-sm text-success">
                      <span>Discount</span>
                      <span>
                        -{getSymbol()}
                        {convert(discount).toLocaleString()}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Shipping</span>
                    <span>
                      {shippingCost === 0 ? (
                        <span className="text-success">Free</span>
                      ) : (
                        `${getSymbol()}${convert(shippingCost).toLocaleString()}`
                      )}
                    </span>
                  </div>

                  {total < 500 && (
                    <p className="text-xs text-muted-foreground">
                      Add {getSymbol()}
                      {convert(500 - total).toLocaleString()} more for free shipping!
                    </p>
                  )}
                </div>

                {/* Total */}
                <div className="flex justify-between items-center py-4 border-t border-border">
                  <span className="text-lg font-semibold">Total</span>
                  <span className="text-2xl font-bold text-brand-orange">
                    {getSymbol()}
                    {convert(finalTotal).toLocaleString()}
                  </span>
                </div>

                {/* Checkout Button */}
                <Link to="/checkout">
                  <Button variant="primary" className="w-full" size="lg">
                    Proceed to Checkout
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>

                {/* Continue Shopping */}
                <Link to="/" className="block text-center mt-4">
                  <Button variant="ghost" className="text-primary">
                    Continue Shopping
                  </Button>
                </Link>

                {/* Trust Badges */}
                <div className="mt-6 pt-4 border-t border-border">
                  <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                    <span>Secure Checkout</span>
                    <span>|</span>
                    <span>SSL Encrypted</span>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  )
}
